import { StrictMode, Suspense } from 'react';
import { config as configureZod } from 'zod/v4/core';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { ApolloProvider } from '@apollo/client';
import { router } from './routes';
import { apolloClient } from './lib/apollo';
import { dismissUpdatingOverlay, stripCacheBustParam } from './lib/update-overlay';
// Side-effect import: configures the i18next instance before first render.
import './i18n';
import './app.css';

// Zod v4 probes `Function('')` on first parse to decide whether it can JIT-compile
// validators. Our CSP has no 'unsafe-eval', so that probe always fails — Zod
// catches it and falls back to the interpreted path, but not before the browser
// files a script-src violation on every page load. Opting out up front keeps the
// violation report stream clean without changing behaviour: the interpreted path
// is what runs under the CSP either way.
configureZod({ jitless: true });

// On a post-update reload, drop the cache-busting query param before the router
// reads the URL so the user lands back on a clean address.
stripCacheBustParam();

const rootElement = document.getElementById('root')!;
createRoot(rootElement).render(
  <StrictMode>
    <ApolloProvider client={apolloClient}>
      <Suspense fallback={null}>
        <RouterProvider router={router} />
      </Suspense>
    </ApolloProvider>
  </StrictMode>
);

// Fade the "Updating…" splash out once the app has painted underneath it (two
// frames guarantees React has committed). No-op on non-update loads.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    dismissUpdatingOverlay();
  });
});
